import React, { useState } from 'react';
import { Power, FileText, Download, AlertTriangle, Users, CheckCircle, PartyPopper } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGCloseButton = ({ 
  selectedAG, 
  token, 
  bureau,
  onAGClosed,
  onDisconnectParticipants,
  onShowSuccessNotification
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState(selectedAG?.status === 'Clôturée');
  const [downloadingPV, setDownloadingPV] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleClose = async () => {
    setClosing(true);
    try {
      const response = await axios.post(`${API}/assemblees/${selectedAG.id}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setClosed(true);
      setShowConfirm(false);
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Notify parent to disconnect non-bureau participants
      if (onDisconnectParticipants && response.data.participants_to_disconnect) {
        onDisconnectParticipants(response.data.participants_to_disconnect);
      }
      
      if (onAGClosed) {
        onAGClosed(response.data);
      }
      
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la clôture');
    } finally {
      setClosing(false);
    }
  };

  const downloadPV = async () => {
    setDownloadingPV(true);
    try {
      // Use fetch instead of axios to avoid responseType issues with Emergent scripts
      const response = await fetch(`${API}/assemblees/${selectedAG.id}/pv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const date = selectedAG.date?.replace(/\//g, '-') || new Date().toISOString().split('T')[0];
      link.setAttribute('download', `PV_AG_${date}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Close success modal after download
      setShowSuccessModal(false);
      
    } catch (err) {
      console.error('Download error:', err);
      alert('Erreur lors du téléchargement du PV');
    } finally {
      setDownloadingPV(false);
    }
  };

  // Check bureau status with detailed info
  const hasPresident = !!bureau?.president;
  const hasScrutateur = bureau?.scrutateurs?.length > 0;
  const hasSecretaire = !!bureau?.secretaire;
  const isBureauComplete = hasPresident && hasScrutateur && hasSecretaire;
  const isBureauEmpty = !hasPresident && !hasScrutateur && !hasSecretaire;
  
  // Count missing members
  const missingMembers = [];
  if (!hasPresident) missingMembers.push('Président');
  if (!hasScrutateur) missingMembers.push('Scrutateur');
  if (!hasSecretaire) missingMembers.push('Secrétaire');

  if (closed || selectedAG?.status === 'Clôturée') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CheckCircle className="text-green-600" size={24} />
            <div>
              <h4 className="font-semibold text-green-800">AG Clôturée</h4>
              <p className="text-sm text-green-600">
                Clôturée le {selectedAG.closed_at ? new Date(selectedAG.closed_at).toLocaleString('fr-FR') : 'maintenant'}
              </p>
            </div>
          </div>
          <button
            onClick={downloadPV}
            disabled={downloadingPV}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {downloadingPV ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            ) : (
              <Download size={18} />
            )}
            <span>Télécharger le PV</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowConfirm(true)}
        className="flex items-center space-x-2 py-2.5 px-4 rounded-lg transition-colors bg-red-600 hover:bg-red-700 text-white"
        data-testid="close-ag-button"
      >
        <Power size={18} />
        <span>Clôturer l'AG</span>
      </button>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Clôturer l'Assemblée Générale ?</h3>
                <p className="text-sm text-gray-500">Cette action est irréversible</p>
              </div>
            </div>
            
            <div className="space-y-3 mb-6">
              {/* Critical warning if bureau is empty */}
              {isBureauEmpty && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 flex items-center space-x-2">
                    <AlertTriangle size={18} />
                    <span>Bureau non constitué</span>
                  </h4>
                  <p className="text-sm text-red-700 mt-2">
                    <strong>Aucun membre du bureau n'a été désigné.</strong> Vous devez constituer le bureau de l'AG 
                    (Président, Scrutateur, Secrétaire) avant de pouvoir clôturer l'assemblée.
                  </p>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    👉 Rendez-vous dans l'onglet "Salle de réunion" pour désigner les membres du bureau.
                  </p>
                </div>
              )}
              
              {/* Warning if bureau is partially complete */}
              {!isBureauComplete && !isBureauEmpty && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <h4 className="font-medium text-amber-800 flex items-center space-x-2">
                    <AlertTriangle size={16} />
                    <span>Bureau incomplet</span>
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Il manque : <strong>{missingMembers.join(', ')}</strong>
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    Le PV sera généré avec les membres présents. Les signatures manquantes seront laissées en blanc.
                  </p>
                </div>
              )}
              
              {/* Success if bureau is complete */}
              {isBureauComplete && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <h4 className="font-medium text-green-800 flex items-center space-x-2">
                    <CheckCircle size={16} />
                    <span>Bureau complet</span>
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    Tous les membres du bureau sont désignés. Le procès-verbal sera généré avec toutes les signatures.
                  </p>
                </div>
              )}
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <h4 className="font-medium text-amber-800 flex items-center space-x-2">
                  <Users size={16} />
                  <span>Participants à distance</span>
                </h4>
                <p className="text-sm text-amber-700 mt-1">
                  Les coproprietaires connectés à distance seront déconnectés de la visioconférence.
                  Seuls les membres du bureau resteront connectés.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="font-medium text-blue-800 flex items-center space-x-2">
                  <FileText size={16} />
                  <span>Procès-Verbal</span>
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Un procès-verbal au format Word sera automatiquement généré et disponible en téléchargement.
                </p>
              </div>
              
              {/* Bureau Summary */}
              {!isBureauEmpty && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="font-medium text-gray-700 mb-2">Bureau de l'AG :</h4>
                  <div className="text-sm space-y-1">
                    <p className={hasPresident ? 'text-green-700' : 'text-red-600'}>
                      {hasPresident ? '✅' : '❌'} Président : <strong>{bureau?.president ? `${bureau.president.prenom} ${bureau.president.nom}` : '(non désigné)'}</strong>
                    </p>
                    <p className={hasScrutateur ? 'text-green-700' : 'text-red-600'}>
                      {hasScrutateur ? '✅' : '❌'} Scrutateur(s) : <strong>{bureau?.scrutateurs?.length > 0 ? bureau.scrutateurs.map(s => `${s.prenom} ${s.nom}`).join(', ') : '(non désigné)'}</strong>
                    </p>
                    <p className={hasSecretaire ? 'text-green-700' : 'text-red-600'}>
                      {hasSecretaire ? '✅' : '❌'} Secrétaire : <strong>{bureau?.secretaire ? `${bureau.secretaire.prenom} ${bureau.secretaire.nom}` : '(non désigné)'}</strong>
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={closing}
                className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {isBureauEmpty ? 'Fermer' : 'Annuler'}
              </button>
              {!isBureauEmpty && (
                <button
                  onClick={handleClose}
                  disabled={closing}
                  className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                  data-testid="confirm-close-ag-btn"
                >
                  {closing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Power size={18} />
                      <span>Confirmer la clôture</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Modal after AG closure */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">
            {/* Top gradient */}
            <div className="h-2 bg-gradient-to-r from-[#00D3A8] via-[#00E5BB] to-[#00C7A1]" />
            
            <div className="p-6">
              {/* Success icon */}
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#00D3A8]/20 to-[#00E5BB]/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-9 h-9 text-[#00D3A8]" />
                  </div>
                  <PartyPopper className="absolute -top-1 -right-1 w-5 h-5 text-amber-400 animate-pulse" />
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-800 text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Assemblée clôturée avec succès !
              </h3>
              <p className="text-gray-600 text-center text-sm mb-5">
                Le procès-verbal a été généré automatiquement. Tous les votes ont été enregistrés.
              </p>

              {/* Next step */}
              <div className="bg-gradient-to-r from-[#00D3A8]/5 to-[#00E5BB]/5 rounded-xl p-4 border border-[#00D3A8]/10">
                <p className="text-xs font-semibold text-[#00D3A8] uppercase tracking-wider mb-1.5">
                  Prochaine étape
                </p>
                <p className="text-gray-700 font-medium text-sm mb-3">
                  Téléchargez le procès-verbal pour l'archiver ou l'envoyer aux coproprietaires.
                </p>
                <button
                  onClick={downloadPV}
                  disabled={downloadingPV}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white py-2.5 px-4 rounded-lg font-medium text-sm hover:shadow-lg hover:shadow-[#00D3A8]/20 transition-all duration-200 disabled:opacity-50"
                  data-testid="success-download-pv-btn"
                >
                  {downloadingPV ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Download size={16} />
                      <span>Télécharger le PV</span>
                    </>
                  )}
                </button>
              </div>

              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full text-gray-500 hover:text-gray-700 py-2.5 mt-3 text-sm font-medium transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AGCloseButton;
